import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';

import classNames from 'classnames';

import {
  Accordion,
  AccordionItem,
  AccordionItemButton,
  AccordionItemHeading,
  AccordionItemPanel,
  AccordionItemState,
} from 'react-accessible-accordion';

import {
  ChevronDownIcon,
  ColumnsIcon,
  GraphIcon,
  LinkExternalIcon,
  PlayIcon,
} from '@primer/octicons-react';
import { StyledOcticon, Tooltip } from '@primer/react';

import orderBy from 'lodash/orderBy';

import { RELATIVE_TIME_RANGES } from '../../../constants/time.constants';

import {
  getLabelForEdgeLocations,
  hasSameLocations,
  sortEdgeLocations,
} from '../../edge/edge_utils';
import changeAssayStatus from '../../../utils/api_functions/changeAssayStatus';
import { getThemeColor } from '../../../utils/theme-utils';
import { formatAssayDetailsDatetime, getTimeRange } from '../../../utils/date_utils';

import useAssayList from '../useAssayList';
import useAssayResults from '../useAssayResults';
import usePipelineData from '../../pipelines/PipelineDataContext';
import useWorkspaceContext from '../../workspaces/WorkspaceContext/WorkspaceContext';

import AssayDetailModal from '../AssayDetailModal/AssayDetailModal';
import AssayFilterControls from '../AssayFilterControls/AssayFilterControls';
import BlankSlate from '../../common/BlankSlate/BlankSlate';
import CardHeader from '../../common/Labels/CardHeader';
import CardView from '../../common/CardView/CardView';
import ErrorSlate from '../../ErrorSlate/ErrorSlate';
import LinkButton from '../../common/Labels/LinkButton';
import LoadingSpinner from '../../common/LoadingSpinner/LoadingSpinner';
import ModalAssayChart from '../ModalAssayChart/ModalAssayChart';
import TimeRangePicker from '../../TimeRangePicker/TimeRangePicker';

import { AssayDropDownOptions } from './AssayDropDownOptions';

import useClientContext from '../../contexts/ClientData/ClientData';
import { usePipelineEdgeData } from '../../VersionHistoryTimeline/PublishPipelineContext';
import getNextRun from '../../../utils/api_functions/getNextRun';

const EmptyListGraphic = () => (
  <StyledOcticon
    className="mb-2 text-brand"
    icon={GraphIcon}
    size={64}
  />
)

const CollapsibleItemContent = ({
  label,
  expanded,
  children,
  actionList,
  totalNumberOfEdges = 0,
  onActionSelect = () => { },
  setCollapsed = () => { },
  showAssayDetails = () => { },
  edgeLocations = [],
  isEdgeEnabled = false,
}) => {
  useEffect(() => {
    setCollapsed(!expanded);
  }, [expanded, setCollapsed]);

  const locationsLabel = getLabelForEdgeLocations(edgeLocations, totalNumberOfEdges);
  const sortedLocations = sortEdgeLocations(edgeLocations);
  const tooltipLocations = sortedLocations.slice(0, 3).map(({ name }) => name);
  const remainingLocations = edgeLocations.length - tooltipLocations.length;
  const tooltipLabel = remainingLocations < 1 ? tooltipLocations.join('\n') : `${tooltipLocations.join('\n')},\n\u2026 & ${remainingLocations} x more`

  return (
    <CardView disableFullHeight>
      <div className="flex justify-between items-center">
        <div className="flex-col justify-between items-center">
          <CardHeader className="mb-2">{label}</CardHeader>
          {
            isEdgeEnabled && (
              <Tooltip direction="e" aria-label={tooltipLabel}>
                <LinkButton
                  onClick={showAssayDetails}
                  showUnderlineOnHover
                  size="sm"
                >
                  {locationsLabel}
                </LinkButton>
              </Tooltip>
            )
          }
        </div>
        <div className="flex items-center">
          <AssayDropDownOptions
            actionListArray={actionList}
            actionListOnSelect={onActionSelect}
          />
          <AccordionItemHeading className="m-1">
            <AccordionItemButton className="p-2 rounded hover:bg-border-light">
              <ChevronDownIcon
                fill={getThemeColor('brand')}
                size={24}
                className={classNames(
                  expanded ? 'chevron-up' : 'chevron-down',
                )}
              />
            </AccordionItemButton>
          </AccordionItemHeading>
        </div>
      </div>
      <AccordionItemPanel>
        {children}
      </AccordionItemPanel>
    </CardView>
  )
}

const CollapsibleItem = ({ children, ...rest }) => (
  <AccordionItem className="w-full my-2 p-1">
    <AccordionItemState>
      {({ expanded }) => (
        <CollapsibleItemContent {...rest} expanded={expanded}>
          {children}
        </CollapsibleItemContent>
      )}
    </AccordionItemState>
  </AccordionItem>
)

const AssayItem = ({
  item,
  refetchAssays,
  handleStatusChange,
  setAssayDetailsToShow,
  setAssayDetailsLastRun,
  initialRange = RELATIVE_TIME_RANGES[7],
}) => {
  const [selectedRun, setSelectedRun] = useState(null);
  const [timeRange, setTimeRange] = useState(initialRange);
  const [collapsed, setCollapsed] = useState(false);

  const assayStatus = item?.active;
  const shouldFetchResults = useCallback(() => !collapsed && assayStatus, [assayStatus, collapsed]);
  const timeRangeArr = useMemo(() => getTimeRange(timeRange), [timeRange]);

  const { isEdgeEnabled } = useClientContext();
  const { listOfEdgesForPipelines } = usePipelineEdgeData();

  const assayLocationIds = item?.locations || item?.window?.locations || [];
  const selectedAssayLocations = !assayLocationIds?.length ? [] : assayLocationIds.map(name => listOfEdgesForPipelines.find(({ name: locationName }) => locationName === name)).filter(loc => loc);
  const totalNumberOfEdges = listOfEdgesForPipelines?.length || 0;
  const { currentWorkspaceId } = useWorkspaceContext();

  const {
    data: resultsData,
    error: resultsError,
    isLoading: areResultsLoading,
    refetchAssayResults,
  } = useAssayResults({
    assayID: item?.id,
    assayConfig: item,
    startTimestamp: timeRangeArr[0],
    endTimestamp: timeRangeArr[1],
    shouldFetchResults,
  })

  const refetch = () => {
    refetchAssays();
    refetchAssayResults();
  }

  const assayActions = [
    {
      id: 'changeAssayStatus',
      label: assayStatus ? 'Pause Assay' : 'Resume Assay',
      icon: assayStatus ? ColumnsIcon : PlayIcon,
    },
    {
      id: 'showDetails',
      label: 'Show Assay Details',
      icon: LinkExternalIcon,
    },
  ];

  const onAssayActionClicked = (actionId) => {
    if (actionId === 'showDetails') {
      setAssayDetailsToShow(item);
      setAssayDetailsLastRun(resultsData[resultsData.length - 1])
    }
    if (actionId === 'changeAssayStatus') {
      handleStatusChange(item.id, currentWorkspaceId, !assayStatus);
      setTimeout(refetchAssays, 1000)
    }
  }

  useEffect(() => {
    if (!collapsed) {
      setTimeRange(initialRange);
    }
  }, [initialRange, collapsed])

  return (
    <CollapsibleItem
      label={item?.name}
      setCollapsed={setCollapsed}
      actionList={assayActions}
      onActionSelect={onAssayActionClicked}
      showAssayDetails={() => onAssayActionClicked('showDetails')}
      edgeLocations={selectedAssayLocations}
      totalNumberOfEdges={totalNumberOfEdges}
      isEdgeEnabled={isEdgeEnabled}
    >
      <div className="m-2">
        <TimeRangePicker
          defaultRange={initialRange}
          selectedRange={timeRange}
          onChange={setTimeRange}
        />
      </div>
      <ModalAssayChart
        assayName={item?.name}
        assayRunResults={resultsData}
        assayDetails={item}
        selectedRun={selectedRun}
        setSelectedRun={setSelectedRun}
        showDetailsFor={setAssayDetailsToShow}
        refetchAssays={refetch}
        message={resultsError}
        isLoading={areResultsLoading}
        loadingText="Loading..."
        showIconMenu
        hideNormalDropdownOptions
        hideCardTitle
      />
    </CollapsibleItem>
  )
}

const AssayChartList = ({
  items,
  setAssayDetailsToShow,
  setAssayDetailsLastRun,
  refetchAssays,
  isAssaysV2Enabled,
  initialRange = RELATIVE_TIME_RANGES[7],
}) => (
  <Accordion
    className="w-full max-h-[48rem]"
    allowMultipleExpanded
    allowZeroExpanded
  >
    {
      items?.map(item => (
        <AssayItem
          key={item.id}
          item={item}
          initialRange={initialRange}
          refetchAssays={refetchAssays}
          setAssayDetailsToShow={setAssayDetailsToShow}
          setAssayDetailsLastRun={setAssayDetailsLastRun}
          handleStatusChange={(a, b, c) => changeAssayStatus(a, b, c, isAssaysV2Enabled)}
        />
      ))
    }
  </Accordion>
)

const LoadingStatesManager = ({
  isListLoading,
  assayData,
  firstAssayRunCompleted,
  assayFilter,
  setAssayDetailsToShow,
  setAssayDetailsLastRun,
  refetchAssays
}) => {
  const { isAssaysV2Enabled } = useClientContext();
  const loadingText = `Loading Assays\u2026`;
  if (isListLoading || (!firstAssayRunCompleted && !assayData?.length)) {
    return (
      <div className="relative h-52">
        <LoadingSpinner
          animationKey="assay-list-loading-spinner"
          loadingText={loadingText}
          useOverlay
        />
      </div>
    )
  }

  return (
    <div className="overflow-scroll flex-grow">
      <div className="mt-6">
        <AssayChartList
          initialRange={assayFilter?.timeRange}
          items={assayData}
          setAssayDetailsToShow={setAssayDetailsToShow}
          setAssayDetailsLastRun={setAssayDetailsLastRun}
          refetchAssays={refetchAssays}
          isAssaysV2Enabled={isAssaysV2Enabled}
        />
      </div>
    </div>
  )
}

const NoListDataWrapper = ({
  children,
  error,
  isListLoading = false,
  numberOfTotalAssays = 0,
  numberOfFilteredAssays = 0,
}) => {
  if (error) {
    return <ErrorSlate errorText={error} />;
  }

  // Loading States Manager component - child of this component
  if (isListLoading) {
    return children;
  }

  if (!numberOfTotalAssays || !numberOfFilteredAssays) {
    const mainText = !numberOfFilteredAssays
      ? 'There are no assays available for the edge locations you have selected'
      : 'There are currently no assays available for this pipeline';

    return (
      <BlankSlate
        className="p-4"
        mainText={mainText}
        subText="Create one to get started!"
        Graphic={EmptyListGraphic}
      />
    )
  }
  return children;
}

export const PipelineAssayChartList = () => {
  const [assayDetailToShow, setAssayDetailsToShow] = useState(null);
  const [assayDetailsLastRun, setAssayDetailsLastRun] = useState(null);
  const [assayFilter, setAssayFilter] = useState({
    name: '',
    active: undefined,
    sortBy: 'CreationDateDesc',
  });
  const [firstAssayRunCompleted, setFirstAssayRunCompleted] = useState(false)

  const { pipeline } = usePipelineData();
  const { isEdgeEnabled, isAssaysV2Enabled } = useClientContext();
  const { listOfEdgesForPipelines, selectedEdgeLocations } = usePipelineEdgeData()
  const { currentWorkspaceId } = useWorkspaceContext();

  const selectedLocations = useMemo(() => !selectedEdgeLocations?.length ? [] : selectedEdgeLocations.map(({ name }) => name), [selectedEdgeLocations]);

  // TODO - Implement location filtering through API parameter when available
  const {
    data: assayData,
    error,
    isLoading: isListLoading,
    firstLoad,
    refetchAssays
  } = useAssayList({
    pipelineID: pipeline?.id,
    pipelineName: pipeline?.pipelineID,
    workspaceID: currentWorkspaceId,
    // locations,
    assayFilter,
  });

  // TODO - Show 'All Edges' if all edge locations are selected (check selected length against edge list length)
  const assaysFilteredByLocation = useMemo(() => {
    /**
     * @deprecated Assays V1 Logic
     */
    if (isAssaysV2Enabled) {
      const filteredAssays = selectedLocations?.length ? assayData.filter(({ locations }) => hasSameLocations(locations || [], selectedLocations)) : assayData;
      return orderBy(filteredAssays, assayItem => assayItem?.locations?.length || 0, ['desc'])
    }
    const filteredAssays = selectedLocations?.length ? assayData.filter(({ window }) => hasSameLocations(window?.locations || [], selectedLocations)) : assayData;
    return orderBy(filteredAssays, assayItem => assayItem.window?.locations?.length || 0, ['desc'])
  }, [assayData, selectedLocations, isAssaysV2Enabled])

  const firstAssay = useMemo(() => {
    if (!assayData?.length) {
      return null;
    }
    return assayData[assayData.length - 1]
  }, [assayData]);

  const firstAssayLastRun = firstAssay?.lastRun;
  const firstAssayStatus = firstAssay?.status;

  useEffect(() => {
    if (!firstAssay?.id) {
      return;
    }
    if (isAssaysV2Enabled && firstAssay?.id) {
      (async () => {
        const times = await getNextRun(firstAssay.id);
        const lastRun = times?.last_run && formatAssayDetailsDatetime(times?.last_run);

        if (lastRun) {
          setFirstAssayRunCompleted(true)
        }
      })();
      /**
       * @deprecated Assays V1 Logic
       */
    } else {
      const lastRun = formatAssayDetailsDatetime(firstAssay.lastRun ?? assayDetailsLastRun?.window_start)
      if (lastRun) {
        setFirstAssayRunCompleted(true)
      }
    }
  }, [firstAssay, assayDetailsLastRun, isAssaysV2Enabled])

  // Fallback logic if get_next_run doesn't give us what we need
  useEffect(() => {
    if (firstAssayLastRun || firstAssayStatus === true) {
      setFirstAssayRunCompleted(true)
    }
  }, [firstAssayLastRun, firstAssayStatus])

  return (
    <>
      <div className="relative flex flex-col">
        <NoListDataWrapper
          error={error}
          numberOfTotalAssays={assayData?.length || 0}
          numberOfFilteredAssays={assaysFilteredByLocation?.length || 0}
          isListLoading={isListLoading || firstLoad}
        >
          <>
            <AssayFilterControls onFilterControlChanged={setAssayFilter} />
            <LoadingStatesManager
              isListLoading={isListLoading || firstLoad}
              assayData={assaysFilteredByLocation}
              firstAssayRunCompleted={firstAssayRunCompleted}
              assayFilter={assayFilter}
              setAssayDetailsToShow={setAssayDetailsToShow}
              setAssayDetailsLastRun={setAssayDetailsLastRun}
              refetchAssays={refetchAssays}
            />
          </>
        </NoListDataWrapper>
      </div>
      <AssayDetailModal
        isShown={!!assayDetailToShow}
        assayDetailsLastRun={assayDetailsLastRun}
        onModalToggle={() => setAssayDetailsToShow(null)}
        assayDetails={assayDetailToShow}
        edgeLocations={listOfEdgesForPipelines}
        isEdgeEnabled={isEdgeEnabled}
      />
    </>
  )
};

export default PipelineAssayChartList;
